import type { CartItem } from '../store/usePosStore';
import type { CartEvaluationOptions } from '../../promotions/domain/cartEvaluationOptions';
import type {
  CartEvaluationResult,
  PricedCartLine,
  PromotionWithTargets,
} from '../../promotions/domain/promotionTypes';
import { normalizePromoCode } from '../../promotions/domain/cartEvaluationOptions';
import {
  evaluateCart,
  estimatePromotionBenefit,
  buildTargetsByPromotionId,
  checkPromotionConditions,
  isPromotionScheduleActive,
  promotionAppliesToBranch,
  promotionAppliesToChannel,
} from '../../promotions/application/promotionEngine';

const AUTO_KINDS = new Set(['percent_off', 'fixed_off', 'combo_buy_x_get_y', 'combo_n_for_price']);

/** Mismo offset que `evaluate-sale-promotions` (Edge) — hora Argentina en schedule/weekdays. */
export const POS_PROMO_TIMEZONE_OFFSET_MINUTES = -180;

function filterPosChannelPromotions(promotions: PromotionWithTargets[]): PromotionWithTargets[] {
  return promotions.filter((promo) => promotionAppliesToChannel(promo, 'pos'));
}

export function mapPosCartToPricedLines(cart: CartItem[]): PricedCartLine[] {
  return cart.map((item) => ({
    lineId: item.lineId,
    productId: item.productId,
    variantId: item.variantId ?? null,
    quantity: item.quantity,
    unitPrice: item.price,
    categoryId: item.category || null,
    brandId: item.brandId ?? null,
    itemType: item.itemType,
    isMisc: item.sku === 'VARIOS',
  }));
}

export function evaluatePosCartPromotions(input: {
  cart: CartItem[];
  promotions: PromotionWithTargets[];
  branchId: string;
  options?: CartEvaluationOptions;
  now?: Date;
}): CartEvaluationResult | null {
  if (input.cart.length === 0) return null;
  const pricedLines = mapPosCartToPricedLines(input.cart);
  return evaluateCart(
    pricedLines,
    filterPosChannelPromotions(input.promotions),
    input.now ?? new Date(),
    input.branchId,
    {
      timezoneOffsetMinutes: POS_PROMO_TIMEZONE_OFFSET_MINUTES,
      currency: input.options?.currency,
      ...input.options,
    },
  );
}

function isEligiblePromotionBase(
  promo: PromotionWithTargets,
  branchId: string,
  now: Date
): boolean {
  return (
    promo.isActive &&
    !promo.deletedAt &&
    AUTO_KINDS.has(promo.kind) &&
    promotionAppliesToChannel(promo, 'pos') &&
    isPromotionScheduleActive(promo, now) &&
    promotionAppliesToBranch(promo, branchId)
  );
}

function buildEvaluationLines(cart: CartItem[]) {
  const pricedLines = mapPosCartToPricedLines(cart);
  const lines = pricedLines.map((line) => ({
    lineId: line.lineId,
    productId: line.productId,
    categoryId: line.categoryId,
    brandId: line.brandId,
    unitPriceOriginal: line.unitPrice,
    quantity: line.quantity,
    lineDiscountTotal: 0,
    appliedDiscounts: [],
    hasNonStackablePromo: false,
  }));
  return { pricedLines, lines };
}

export function findPromotionByCode(
  code: string,
  promotions: PromotionWithTargets[]
): PromotionWithTargets | null {
  const normalized = normalizePromoCode(code);
  if (!normalized) return null;
  return (
    promotions.find(
      (promo) =>
        promo.applyMode === 'manual_code' &&
        promo.promoCode != null &&
        promo.promoCode.trim().toUpperCase() === normalized
    ) ?? null
  );
}

export function listCashierSelectablePromotions(
  cart: CartItem[],
  promotions: PromotionWithTargets[],
  branchId: string,
  now: Date = new Date(),
  usageCountByPromotionId?: Map<string, number>
): PromotionWithTargets[] {
  if (cart.length === 0) return [];
  const { pricedLines, lines } = buildEvaluationLines(cart);
  const posPromotions = filterPosChannelPromotions(promotions);
  const targetsByPromoId = buildTargetsByPromotionId(posPromotions);

  return posPromotions.filter((promo) => {
    if (promo.applyMode !== 'cashier_select') return false;
    if (!isEligiblePromotionBase(promo, branchId, now)) return false;
    if (
      !checkPromotionConditions(
        promo,
        pricedLines,
        now,
        usageCountByPromotionId?.get(promo.id),
      )
    ) {
      return false;
    }
    const targets = targetsByPromoId.get(promo.id) ?? [];
    return estimatePromotionBenefit(promo, targets, pricedLines, lines) > 0;
  });
}

const WEEKDAY_SHORT: Record<number, string> = {
  1: 'lun',
  2: 'mar',
  3: 'mié',
  4: 'jue',
  5: 'vie',
  6: 'sáb',
  7: 'dom',
};

export function listInactiveCashierPromoHints(
  promotions: PromotionWithTargets[],
  branchId: string,
  now: Date = new Date()
): { id: string; name: string; reason: string }[] {
  const minutesToTime = (t: string): number => {
    const [h, m] = t.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
  };
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const nowDay = now.getDay() === 0 ? 7 : now.getDay();

  const dailyBlockReason = (promo: PromotionWithTargets): string | null => {
    const c = promo.conditions ?? {};
    if (c.weekdays && c.weekdays.length > 0 && !c.weekdays.includes(nowDay as 1 | 2 | 3 | 4 | 5 | 6 | 7)) {
      const days = [...c.weekdays]
        .sort((a, b) => a - b)
        .map((d) => WEEKDAY_SHORT[d] ?? String(d))
        .join(', ');
      return `Solo ${days}`;
    }
    const w = c.scheduleWindow;
    if (w?.startTime && w?.endTime) {
      const s = minutesToTime(w.startTime);
      const e = minutesToTime(w.endTime);
      const inWindow = e >= s ? nowMinutes >= s && nowMinutes <= e : nowMinutes >= s || nowMinutes <= e;
      if (!inWindow) return `Disponible ${w.startTime}–${w.endTime}`;
    }
    return null;
  };

  return filterPosChannelPromotions(promotions)
    .filter(
      (promo) =>
        promo.applyMode === 'cashier_select' &&
        promo.isActive &&
        !promo.deletedAt &&
        AUTO_KINDS.has(promo.kind) &&
        promotionAppliesToBranch(promo, branchId) &&
        isPromotionScheduleActive(promo, now)
    )
    .map((promo) => ({ id: promo.id, name: promo.name, reason: dailyBlockReason(promo) }))
    .filter((h): h is { id: string; name: string; reason: string } => h.reason != null);
}

export function validatePromoCodeForCart(
  code: string,
  cart: CartItem[],
  promotions: PromotionWithTargets[],
  branchId: string,
  now: Date = new Date(),
  usageCountByPromotionId?: Map<string, number>
): { ok: true; promotion: PromotionWithTargets } | { ok: false; message: string } {
  const promotion = findPromotionByCode(code, promotions);
  if (!promotion) {
    return { ok: false, message: 'Código no válido' };
  }
  if (!isEligiblePromotionBase(promotion, branchId, now)) {
    return { ok: false, message: 'La promoción no está vigente en esta sucursal' };
  }
  if (cart.length === 0) {
    return { ok: false, message: 'Agregá ítems al carrito' };
  }
  const { pricedLines, lines } = buildEvaluationLines(cart);
  if (
    !checkPromotionConditions(
      promotion,
      pricedLines,
      now,
      usageCountByPromotionId?.get(promotion.id),
    )
  ) {
    return { ok: false, message: 'Se alcanzó el límite de usos de esta promo' };
  }
  const targets = buildTargetsByPromotionId(promotions).get(promotion.id) ?? [];
  if (estimatePromotionBenefit(promotion, targets, pricedLines, lines) <= 0) {
    return { ok: false, message: 'No aplica a este carrito' };
  }
  return { ok: true, promotion };
}
